import React, { useCallback, useEffect, useMemo } from "react";
import { Grid, Icon, Popup, Select } from "semantic-ui-react";
import { getNetworkIpVersion } from "../../app_shared_functions";
import { getSelectItemClassName } from "../../shared-functions/string";
import { useSelector } from "react-redux";
import useSubscribe from "../../custom-hooks/useSubscribe";
import { ExternalIPProps, Network } from "./types";
import NoNetworks from "../servers/create/network/NoNetworks";

const ExternalIP: React.FC<ExternalIPProps> = ({
  handleChange,
  inputKey,
  value,
  title,
  isRequired,
  disabled,
  region,
  description,
}) => {
  useSubscribe("NETWORKS_LIST");

  const networks = useSelector(
    (state: any) => Object.values(state.networks.NETWORKS_LIST) as Network[],
  );

  const usableNetworks = useMemo(() => {
    return networks.filter(
      (network) =>
        network?.router?.external_gateway_info &&
        network.region?.toLowerCase() === region.toLowerCase(),
    );
  }, [networks, region]);

  // Clear the selection if it is invalid (e.g., when there are no enabled networks).
  useEffect(() => {
    const enabledNetworks = usableNetworks.filter(
      (net) => getNetworkIpVersion(net) !== 6,
    );
    if (enabledNetworks.length === 0 && value && value.networkId !== "") {
      handleChange({
        name: inputKey,
        value: { networkId: "", externalId: "" },
      });
    }
  }, [usableNetworks, value, handleChange, inputKey]);

  const handleSelectChange = useCallback(
    (e, d) => {
      const selectedNetwork = usableNetworks.find((net) => net.id === d.value);
      const selectedValue = {
        networkId: selectedNetwork?.id ?? "",
        externalId:
          selectedNetwork?.router?.external_gateway_info?.network_id ?? "",
      };
      handleChange({
        name: inputKey,
        value: selectedValue,
      });
    },
    [handleChange, inputKey, usableNetworks],
  );

  const selectOptions = useMemo(
    () =>
      usableNetworks.map((net) => ({
        disabled: getNetworkIpVersion(net) === 6,
        key: net.id,
        value: net.id,
        text: net.name,
        className: getSelectItemClassName(net.name),
      })),
    [usableNetworks],
  );

  if (usableNetworks.length === 0) {
    return <NoNetworks networks={usableNetworks} region={region} />;
  }

  return (
    <Grid.Row>
      <Grid.Column width={8} className="flex vcenter margin-top-00">
        <h5>
          {title}{" "}
          {description && (
            <Popup
              content={description}
              trigger={
                <Icon
                  name="info circle"
                  size="small"
                  style={{ marginLeft: "0.5em", cursor: "pointer" }}
                />
              }
            />
          )}
          {isRequired && (
            <span className="color-red padding-left-half bold">*</span>
          )}
        </h5>
      </Grid.Column>
      <Grid.Column width={8} className="flex vcenter margin-top-00">
        <Select
          icon="chevron circle down"
          className="select-box full"
          placeholder="Select External Network"
          options={selectOptions}
          disabled={disabled}
          value={value ? value.networkId : ""}
          onChange={handleSelectChange}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

export default ExternalIP;
